import { useMemo } from 'react';

import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';

import { COLOR, FONT } from '@/shared/design-tokens/design-tokens';
import { formatMetricValue } from '@/shared/lib/utils/number';

import type { GpuTempMetric } from '@/features/monitoring/api/types';

import styles from './GpuTempGauge.module.css';

type GpuTempGaugeProps = {
  data: GpuTempMetric;
  height?: number;
};

const MAX_TEMP = 100;
const WARNING_TEMP = 83;
const DANGER_TEMP = 87;

export const GpuTempGauge = ({ data, height = 120 }: GpuTempGaugeProps) => {
  const chartOption: EChartsOption = useMemo(() => {
    const getColor = () => {
      if (data.temperature >= DANGER_TEMP) return COLOR.RED[50];
      if (data.temperature >= WARNING_TEMP) return COLOR.ORANGE[50];
      return COLOR.GREEN[50];
    };

    return {
      series: [
        {
          type: 'gauge',
          radius: '100%',
          center: ['50%', '70%'],
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: MAX_TEMP,
          itemStyle: {
            color: getColor(),
            shadowColor: `${getColor()}80`,
            shadowBlur: 10,
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },
          progress: {
            show: true,
            width: 18,
          },
          pointer: {
            show: false,
          },
          axisLine: {
            lineStyle: {
              width: 18,
              color: [[1, COLOR.GRAY[90]]],
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: false,
          },
          anchor: {
            show: false,
          },
          title: {
            show: false,
          },
          detail: {
            valueAnimation: true,
            width: '60%',
            lineHeight: 40,
            borderRadius: 8,
            offsetCenter: [0, '-5%'],
            fontSize: FONT.SIZE.LG,
            fontWeight: FONT.WEIGHT.EXTRA_BOLD,
            fontFamily: FONT.FAMILY.NANUMSQUARE,
            formatter: () => `${formatMetricValue(data.temperature)}°C`,
            color: getColor(),
          },
          data: [
            {
              value: data.temperature,
            },
          ],
        },
        {
          type: 'gauge',
          radius: '100%',
          center: ['50%', '70%'],
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: MAX_TEMP,
          itemStyle: {
            color: 'transparent',
          },
          progress: {
            show: false,
          },
          pointer: {
            show: false,
          },
          axisLine: {
            show: true,
            lineStyle: {
              width: 18,
              color: [
                [WARNING_TEMP / MAX_TEMP, `${COLOR.GREEN[50]}25`],
                [DANGER_TEMP / MAX_TEMP, `${COLOR.ORANGE[50]}25`],
                [1, `${COLOR.RED[50]}25`],
              ],
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: false,
          },
          detail: {
            show: false,
          },
          data: [{ value: 0 }],
        },
      ],
    };
  }, [data.temperature]);

  return (
    <div className={styles.gaugeContainer}>
      <div className={styles.gaugeWrapper}>
        <ReactECharts
          option={chartOption}
          style={{ height: `${height}px`, width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
        />
      </div>
      <div className={styles.gpuLabel}>{data.displayName}</div>
    </div>
  );
};
